import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchSkusList } from '@/api/my-styles';
import type { SkusListParams } from '@/api/my-styles/types';
import { useNetworkCompanyMap } from '@/hooks/useDropdowns';
import { buildCodesFilter } from '../-utils/codesFilter';

export interface SkusQueryInput {
  page: number;
  limit: number;
  isArchived?: boolean;
  /** When `true`, filter to component-SKU entities (My Components route);
   *  when false / undefined, returns style SKUs. */
  isComponent?: boolean;
  sort?: string;
  filters?: Record<string, string[]>;
}

function parseSort(sort?: string): { sort: string; order: 'ASC' | 'DESC' } {
  if (!sort) return { sort: 'name', order: 'ASC' };
  const [field, dir] = sort.split(':');
  if (!field) return { sort: 'name', order: 'ASC' };
  return { sort: field, order: dir === 'DESC' ? 'DESC' : 'ASC' };
}

function nonEmpty(arr?: string[]): string[] | undefined {
  return arr && arr.length > 0 ? arr : undefined;
}

function buildApiParams(input: SkusQueryInput): SkusListParams {
  const parsed = parseSort(input.sort);
  const f = input.filters ?? {};

  const suppliers = nonEmpty(f.suppliers);
  const vendors = nonEmpty(f.vendors);
  const brands = nonEmpty(f.brands);
  const departments = nonEmpty(f.departments);
  const seasons = nonEmpty(f.seasons);
  const collections = nonEmpty(f.collections);
  // Parent-variant filter is a multi-select checkbox list — send every chosen
  // variant, not just the first (legacy endpoint accepts the `variantIds` array).
  const variantIds = nonEmpty(f.variantId);
  // Same for the parent-style filter: the state key is `styleId`, but the
  // endpoint only knows the plural `styleIds` array.
  const styleIds = nonEmpty(f.styleId);

  const codes = buildCodesFilter(f);
  const isStoryActiveRaw = f.isStoryActive?.[0];
  const isStoryActive =
    isStoryActiveRaw === 'true'
      ? true
      : isStoryActiveRaw === 'false'
        ? false
        : undefined;

  return {
    page: input.page,
    limit: input.limit,
    // 'variant' + 'style' populate row.variant / row.style so the Parent
    // Variant / Parent Style columns render (mirrors old frontend parentLevels).
    include: [
      'type',
      'styleProperties',
      'material',
      'tracingStory',
      'variant',
      'style',
    ],
    isArchived: input.isArchived ?? false,
    isComponent: input.isComponent ?? false,
    sort: parsed.sort,
    order: parsed.order,
    ...(f.name?.[0] !== undefined ? { name: f.name[0] } : {}),
    ...(f.size?.[0] !== undefined ? { size: f.size[0] } : {}),
    ...(f.color?.[0] !== undefined ? { color: f.color[0] } : {}),
    ...(variantIds !== undefined ? { variantIds } : {}),
    ...(styleIds !== undefined ? { styleIds } : {}),
    ...(f.styleTypeId?.[0] !== undefined
      ? { styleTypeId: f.styleTypeId[0] }
      : {}),
    ...(f.mainSupplier?.[0] !== undefined
      ? { mainSupplier: f.mainSupplier[0] }
      : {}),
    ...(suppliers !== undefined ? { suppliers } : {}),
    ...(vendors !== undefined ? { vendors } : {}),
    ...(brands !== undefined ? { brands } : {}),
    ...(departments !== undefined ? { departments } : {}),
    ...(seasons !== undefined ? { seasons } : {}),
    ...(collections !== undefined ? { collections } : {}),
    ...(codes !== undefined ? { codes } : {}),
    ...(isStoryActive !== undefined ? { isStoryActive } : {}),
  };
}

export function skusQueryOptions(input: SkusQueryInput) {
  const apiParams = buildApiParams(input);
  return queryOptions({
    queryKey: ['my-skus', 'list', apiParams] as const,
    queryFn: () => fetchSkusList(apiParams),
  });
}

export function useSkusQuery(input: SkusQueryInput) {
  const query = useQuery(skusQueryOptions(input));
  const companyMap = useNetworkCompanyMap();
  return { query, companyMap };
}
